import { useState, useEffect, useCallback } from 'preact/hooks'
import { Loader2, Save, Check, AlertCircle } from 'lucide-preact'
import { salesforceApi } from '@/api/salesforce'
import {
    ENGAGEMENT_STATES,
    ENGAGEMENT_STATE_LABELS,
    WEB_EVENT_TYPES,
    WEB_EVENT_TYPE_LABELS,
} from '@/types/salesforce'
import type {
    EngagementState,
    WebEventType,
    SalesforceMappingConfig,
    SalesforceCampaignConfig,
} from '@/types/salesforce'

interface SalesforceCampaignConfigSectionProps {
    clientCompanyId: string
}

/**
 * Campaign configuration for a connected Salesforce org (ORB-659).
 *
 * OrbitalX creates one Campaign per landing page and adds each person as a Campaign Member. Most
 * Salesforce orgs already have their own names for the member statuses we care about — Attendee,
 * RSVP'd, Non-attendee — so this maps ours onto theirs rather than writing ours into their org.
 *
 * Everything here is optional. The form loads pre-filled with what OrbitalX would write if the admin
 * changes nothing, so it doubles as a readout of current behaviour.
 */
export function SalesforceCampaignConfigSection({ clientCompanyId }: SalesforceCampaignConfigSectionProps) {
    const [config, setConfig] = useState<SalesforceMappingConfig | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [isSaved, setIsSaved] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const load = useCallback(async () => {
        setIsLoading(true)
        setError(null)
        try {
            // apiClient resolves with an `error` field on a non-OK response rather than throwing, so
            // the failure path is a branch, not a catch. The catch below is only for a genuine throw.
            const response = await salesforceApi.getConfig(clientCompanyId)
            if (response.error || !response.data) {
                setError(response.error ?? 'Could not load the campaign settings.')
                setConfig(null)
                return
            }
            setConfig(response.data)
        } catch {
            setError('Could not load the campaign settings. Close and reopen the panel to try again.')
            setConfig(null)
        } finally {
            setIsLoading(false)
        }
    }, [clientCompanyId])

    useEffect(() => { load() }, [load])

    const campaign = config?.Campaign

    const updateCampaign = (patch: Partial<SalesforceCampaignConfig>) => {
        if (!config?.Campaign) return
        setIsSaved(false)
        setConfig({ ...config, Campaign: { ...config.Campaign, ...patch } })
    }

    const setStatusLabel = (state: EngagementState, statusLabel: string) => {
        if (!campaign) return
        const existing = campaign.MemberStatuses.some(m => m.EngagementState === state)
        const memberStatuses = existing
            ? campaign.MemberStatuses.map(m => (m.EngagementState === state ? { ...m, StatusLabel: statusLabel } : m))
            : [...campaign.MemberStatuses, { EngagementState: state, StatusLabel: statusLabel }]
        updateCampaign({ MemberStatuses: memberStatuses })
    }

    const setCampaignType = (eventType: WebEventType, campaignType: string) => {
        if (!campaign) return
        // Trimmed on the way in, not just when deciding whether to keep the row: a picklist value with
        // stray whitespace looks right in the form and is rejected by Salesforce.
        const trimmed = campaignType.trim()
        const others = campaign.CampaignTypes.filter(t => t.EventType !== eventType)
        // An empty value removes the row rather than saving a blank. Leaving Type unset is a supported
        // outcome; writing "" into the customer's picklist is not.
        updateCampaign({ CampaignTypes: trimmed ? [...others, { EventType: eventType, CampaignType: trimmed }] : others })
    }

    const statusLabelFor = (state: EngagementState) =>
        campaign?.MemberStatuses.find(m => m.EngagementState === state)?.StatusLabel ?? ''

    const campaignTypeFor = (eventType: WebEventType) =>
        campaign?.CampaignTypes.find(t => t.EventType === eventType)?.CampaignType ?? ''

    const handleSave = async () => {
        if (!config) return
        setIsSaving(true)
        setError(null)
        // Cleared per attempt rather than only in the failure branch, so it also covers a throw. A
        // stale "Saved" sitting beside a fresh error is worse than either on its own.
        setIsSaved(false)
        try {
            const response = await salesforceApi.saveConfig(clientCompanyId, config)
            if (response.error) {
                // The API names the specific problem — a duplicate label, an unknown state — which is
                // far more use to an admin than a generic failure. Never claim success over it.
                setError(response.error)
                return
            }
            setIsSaved(true)
        } catch {
            setError('Could not save the campaign settings.')
        } finally {
            setIsSaving(false)
        }
    }

    if (isLoading) {
        return (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 flex items-center gap-3">
                <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
                <span className="text-sm text-slate-500 dark:text-slate-400">Loading campaign settings…</span>
            </div>
        )
    }

    if (!campaign) {
        return (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <p role="alert" className="text-sm text-slate-500 dark:text-slate-400">
                    {error ?? 'Campaign settings are unavailable.'}
                </p>
            </div>
        )
    }

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-6">
            <div>
                <h4 className="font-medium text-sm text-slate-900 dark:text-white">Campaigns</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    OrbitalX creates one Salesforce campaign per landing page and adds each person as a
                    campaign member. If your org already has its own names for these, set them here and
                    we'll use yours instead.
                </p>
            </div>

            <MemberStatusFields valueFor={statusLabelFor} onChange={setStatusLabel} />
            <CampaignTypeFields valueFor={campaignTypeFor} onChange={setCampaignType} />
            <PlacementFields campaign={campaign} onChange={updateCampaign} />

            {error && (
                <div role="alert" className="flex items-start gap-2 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            <div className="flex items-center gap-3 pt-1">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50"
                >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {isSaving ? 'Saving…' : 'Save campaign settings'}
                </button>
                {isSaved && !isSaving && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                        <Check className="w-4 h-4" /> Saved
                    </span>
                )}
            </div>
        </div>
    )
}

/**
 * One row per canonical engagement state. These are added to every campaign we create, alongside the
 * Sent and Responded that Salesforce adds itself — the customer's own are never changed or removed.
 */
function MemberStatusFields({ valueFor, onChange }: {
    valueFor: (state: EngagementState) => string
    onChange: (state: EngagementState, value: string) => void
}) {
    return (
        <Section
            title="Member statuses"
            hint="We add these to every campaign we create, alongside the Sent and Responded that Salesforce adds itself. Yours are never changed or removed."
        >
            {ENGAGEMENT_STATES.map(state => (
                <RowField
                    key={state}
                    label={ENGAGEMENT_STATE_LABELS[state]}
                    value={valueFor(state)}
                    onChange={value => onChange(state, value)}
                />
            ))}
        </Section>
    )
}

/** WebType → the customer's Campaign.Type picklist value. Blank means don't set Type at all. */
function CampaignTypeFields({ valueFor, onChange }: {
    valueFor: (eventType: WebEventType) => string
    onChange: (eventType: WebEventType, value: string) => void
}) {
    return (
        <Section
            title="Campaign type"
            hint="Optional. Leave a row blank and we won't set a type on that campaign at all — safer than guessing at a value your picklist may not allow."
        >
            {WEB_EVENT_TYPES.map(eventType => (
                <RowField
                    key={eventType}
                    label={WEB_EVENT_TYPE_LABELS[eventType]}
                    value={valueFor(eventType)}
                    placeholder="Leave blank to skip"
                    onChange={value => onChange(eventType, value)}
                />
            ))}
        </Section>
    )
}

function PlacementFields({ campaign, onChange }: {
    campaign: SalesforceCampaignConfig
    onChange: (patch: Partial<SalesforceCampaignConfig>) => void
}) {
    return (
        <Section
            title="Placement"
            hint="A parent campaign is the useful one — it lets you include or exclude everything OrbitalX creates with a single filter in your reports."
        >
            <StackedField
                label="Campaign name prefix"
                hint="Added to the front of every campaign name. Renaming is safe at any time."
                value={campaign.NamePrefix ?? ''}
                placeholder="e.g. [OrbitalX]"
                onChange={value => onChange({ NamePrefix: value || null })}
            />
            <StackedField
                label="Parent campaign ID"
                hint="An 18-character Salesforce campaign ID. Leave blank for no parent."
                value={campaign.ParentCampaignId ?? ''}
                placeholder="701…"
                onChange={value => onChange({ ParentCampaignId: value || null })}
            />
            <StackedField
                label="Campaign owner ID"
                hint="A Salesforce user or queue ID. Leave blank to use whoever connected OrbitalX."
                value={campaign.OwnerId ?? ''}
                placeholder="005…"
                onChange={value => onChange({ OwnerId: value || null })}
            />
        </Section>
    )
}

function Section({ title, hint, children }: { title: string; hint: string; children: preact.ComponentChildren }) {
    return (
        <section className="space-y-3">
            <div>
                <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h5>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{hint}</p>
            </div>
            <div className="space-y-2">{children}</div>
        </section>
    )
}

function RowField({ label, value, placeholder, onChange }: {
    label: string
    value: string
    placeholder?: string
    onChange: (value: string) => void
}) {
    return (
        <label className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:items-center">
            <span className="text-xs text-slate-600 dark:text-slate-300">{label}</span>
            <input
                type="text"
                value={value}
                placeholder={placeholder}
                aria-label={label}
                onInput={e => onChange((e.target as HTMLInputElement).value)}
                className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            />
        </label>
    )
}

function StackedField({ label, hint, value, placeholder, onChange }: {
    label: string
    hint: string
    value: string
    placeholder?: string
    onChange: (value: string) => void
}) {
    return (
        <label className="block space-y-1">
            <span className="text-xs text-slate-600 dark:text-slate-300">{label}</span>
            <input
                type="text"
                value={value}
                placeholder={placeholder}
                aria-label={label}
                onInput={e => onChange((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            />
            <span className="block text-[11px] text-slate-400 dark:text-slate-500">{hint}</span>
        </label>
    )
}
